import React, {
  KeyboardEvent,
  MouseEvent,
} from 'react';
import {
  Button,
} from '@populateui/ui';
import clsx from 'clsx';
import {
  useNotify,
} from 'core/hooks';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  AlertAction,
  PCCDashboardAlertDTO,
} from 'dtos';
import {
  AlertActionTypes,
} from 'dtos/alertActionTypes';
import {
  PCCProgram,
} from 'dtos/pCCProgram';
import {
  useEnrollPatientInPCC,
  useOptInPatientToCCM,
  useSendPCCReminder,
} from 'views/EMR/hooks/pcc';
import {
  normalizeAlerts,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/helper';
import {
  PCCPatientActionContext,
} from 'pages/Dashboard/pages/Dashboard/PCCOverview/types';
import {
  patientEventChannel,
  pccEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

type Props = {
  alerts?: PCCDashboardAlertDTO[] | null;
  patient: PCCPatientActionContext;
  className?: string;
};

type PCCAlertActionDefinition = {
  variant: 'outlined' | 'contained' | 'text';
  run: () => void;
};

const supportedPCCAlertActionTypes = [
  AlertActionTypes.ResendPCCReminder,
  AlertActionTypes.SendPCCConsentForms,
  AlertActionTypes.EnrollInPCC,
  AlertActionTypes.ManuallyOptInToCCM,
] as const;

type SupportedPCCAlertActionType = typeof supportedPCCAlertActionTypes[number];

export function isSupportedPCCAlertAction(
  action: AlertAction,
): action is AlertAction & { type: SupportedPCCAlertActionType } {
  return supportedPCCAlertActionTypes.some((type) => type === action.type);
}

function getAlertActions(alerts?: PCCDashboardAlertDTO[] | null) {
  return normalizeAlerts(alerts).flatMap(({ alert, key }) => (alert.availableActions ?? [])
    .filter(isSupportedPCCAlertAction)
    .map((action) => ({
      action,
      key: `${key}:${action.type ?? ''}`,
    })));
}

export default function PCCAlertActions({
  alerts,
  patient,
  className,
}: Props) {
  const notify = useNotify();
  const {
    mutateAsync: sendPCCReminder,
    isPending: isSendingReminder,
  } = useSendPCCReminder();
  const {
    mutateAsync: optInPatientToCCM,
    isPending: isOptingIn,
  } = useOptInPatientToCCM();
  const {
    mutateAsync: enrollPatient,
    isPending: isEnrolling,
  } = useEnrollPatientInPCC();
  const actions = getAlertActions(alerts);
  const hasActions = actions.length > 0;
  const isPending = isSendingReminder || isOptingIn || isEnrolling;
  const canRunAction = patient.patientId > 0 && !isPending;

  const { requiresRTMEnrollmentDetails } = useEMRAccess();

  const handleResendReminder = async () => {
    try {
      await sendPCCReminder({ patientId: patient.patientId });
      notify({
        variant: 'success',
        message: 'PCC reminder sent.',
      });
    } catch {
      // Global mutation onError handles error notify
    }
  };

  const handleSendConsentForms = () => {
    patientEventChannel.emit('onShowPCCConsentForm', patient);
  };

  const handleEnrollInPCC = async () => {
    const selectedPrograms = patient.programs ?? [];

    if (selectedPrograms.includes(PCCProgram.RTM) && requiresRTMEnrollmentDetails) {
      pccEventChannel.emit('onShowPCCEnroll', {
        patientId: patient.patientId,
        programs: selectedPrograms,
        selectedPrograms,
        variant: 'recommendation',
      });
    } else if (selectedPrograms.length > 0) {
      try {
        await enrollPatient({
          patientId: patient.patientId,
          programs: selectedPrograms,
        });
        notify({
          variant: 'success',
          message: selectedPrograms.length > 1
            ? 'Patient enrolled in selected PCC programs'
            : `Patient enrolled in ${selectedPrograms[0]}`,
        });
      } catch {
        // Global mutation onError handles error notify
      }
    }
  };

  const handleOptInToCCM = async () => {
    try {
      await optInPatientToCCM({ patientId: patient.patientId });
      notify({
        variant: 'success',
        message: 'Patient opted in to CCM.',
      });
    } catch {
      // Global mutation onError handles error notify
    }
  };

  const actionDefinitions: Record<SupportedPCCAlertActionType, PCCAlertActionDefinition> = {
    [AlertActionTypes.ResendPCCReminder]: {
      variant: 'outlined',
      run: handleResendReminder,
    },
    [AlertActionTypes.SendPCCConsentForms]: {
      variant: 'outlined',
      run: handleSendConsentForms,
    },
    [AlertActionTypes.EnrollInPCC]: {
      variant: 'outlined',
      run: handleEnrollInPCC,
    },
    [AlertActionTypes.ManuallyOptInToCCM]: {
      variant: 'outlined',
      run: handleOptInToCCM,
    },
  };

  const handleActionClick = (
    event: MouseEvent<HTMLButtonElement>,
    action: AlertAction,
  ) => {
    event.stopPropagation();

    if (isSupportedPCCAlertAction(action) && canRunAction) {
      actionDefinitions[action.type].run();
    }
  };

  const stopPropagation = (
    event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>,
  ) => {
    event.stopPropagation();
  };

  return hasActions ? (
    <div
      className={clsx('flex flex-wrap items-center gap-1.5', className)}
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
      role="presentation"
    >
      {actions.map(({ action, key }) => (
        <Button
          key={key}
          type="button"
          variant={actionDefinitions[action.type].variant}
          color="primary"
          size="small"
          disabled={!canRunAction}
          onClick={(event) => { handleActionClick(event, action); }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  ) : (
    <span className="text-xs font-medium text-gray-700">{DEFAULT_EMPTY_VALUE}</span>
  );
}
